import { useCallback, useMemo, useState } from 'react';
import { colour, progressShade, text } from '../../design/tokens.ts';
import { Card, Field, Page, Select, TextInput, Tile, Tiles } from '../../components/basics.tsx';
import { CouldNotLoad, DoneToast, Empty, Loading, NoResults, OfflineBanner } from '../../components/states.tsx';
import { Dialog } from '../../components/Dialog.tsx';
import { api } from '../../api/client.ts';
import { useLoad } from '../../app/useLoad.ts';
import { useCan } from '../../app/me.tsx';
import { Filters } from './Filters.tsx';
import { Heatmap } from './Heatmap.tsx';
import { Rows } from './Rows.tsx';
import type { BoardAnswer, BoardFilter, BoardRow, BoardView, BulkLaunchResult, PassState } from './types.ts';

/**
 * Screen 3: the project board. The screen Maestro is mostly looked at through.
 *
 * Everything is one round trip. The filters become a query on the server, which
 * answers with the counts, a compact cell per matching pass for the heatmap,
 * and one page of full rows — because a splat can have about 2,500 passes and
 * shipping them all to the browser was ruled out in the spec, not discovered
 * later.
 *
 * Bulk launch always shows the totals before it launches anything, whatever the
 * number; above the threshold an admin set, it is a big-job dialog instead of
 * the ordinary one.
 */

const STATE_TILES: { key: PassState; label: string; swatch: string }[] = [
  { key: 'good', label: 'Good, and current', swatch: colour.good },
  { key: 'stale', label: 'Something is stale', swatch: colour.stale },
  { key: 'notmade', label: 'Not made yet', swatch: colour.cellEmpty },
  { key: 'blocked', label: 'Blocked on a VC', swatch: colour.accent }
];

export function Board({
  projectId,
  onOpenPass
}: {
  projectId: string;
  onOpenPass: (passId: string) => void;
}) {
  const can = useCan();
  const [filter, setFilter] = useState<BoardFilter>({ project_id: projectId });
  const [view, setView] = useState<BoardView>('by_sequence');
  const [launching, setLaunching] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [plan, setPlan] = useState<{ step: string; result: BulkLaunchResult } | null>(null);
  const [owner, setOwner] = useState('');
  const [saveName, setSaveName] = useState<string | null>(null);

  const body = useMemo(() => ({ filter, view, limit: 100 }), [filter, view]);
  const { state, reload } = useLoad<BoardAnswer>(
    () => api.post<BoardAnswer>('/api/board', body),
    [JSON.stringify(body)]
  );

  /**
   * What the dropdowns offer. Separate from the board because it does not
   * change as the filters do — and the VCs are per splat, because `cam0003` in
   * one splat is not `cam0003` in another.
   */
  const { state: scope } = useLoad<{
    splats: { splat_id: string; says: string; version: number }[];
    sequences: { _id: string; name: string }[];
    vcs: { vc_id: string; name?: string }[];
  }>(async () => {
    const [splats, sequences] = await Promise.all([
      api.get<{ splats: { splat_id: string; says: string; version: number }[] }>(
        `/api/splats?project_id=${encodeURIComponent(projectId)}`
      ),
      api.get<{ sequences: { _id: string; name: string }[] }>(
        `/api/sequences?project_id=${encodeURIComponent(projectId)}`
      )
    ]);
    const splatId = filter.splat_id ?? splats.splats[0]?.splat_id;
    const vcs = splatId
      ? await api
          .get<{ vcs: { vc_id: string; name?: string }[] }>(`/api/splats/${splatId}/vcs`)
          .then((b) => b.vcs)
          .catch(() => [])
      : [];
    return { splats: splats.splats, sequences: sequences.sequences, vcs };
  }, [projectId, filter.splat_id]);

  const answer = state.data;
  // Everybody who owns a step of something on the board. Taken from the rows
  // rather than from /api/users, which only an admin may read.
  const people = useMemo(
    () => [...new Set((answer?.rows ?? []).flatMap((row) => row.owners))].sort(),
    [answer]
  );

  const launchOne = useCallback(
    async (passId: string, stepKey: string) => {
      setLaunching(`${passId}:${stepKey}`);
      try {
        await api.post(`/api/passes/${passId}/steps/${stepKey}/launch`, {});
        setToast('Launched. It is on the Runs screen.');
        reload();
      } catch (err) {
        setToast(err instanceof Error ? err.message : 'That could not be launched.');
      } finally {
        setLaunching(null);
      }
    },
    [reload]
  );

  const planBulk = useCallback(
    async (step: string) => {
      try {
        const result = await api.post<BulkLaunchResult>('/api/board/launch', { filter, step });
        setPlan({ step, result });
      } catch (err) {
        setToast(err instanceof Error ? err.message : 'That could not be planned.');
      }
    },
    [filter]
  );

  const confirmBulk = useCallback(async () => {
    if (!plan) return;
    try {
      const result = await api.post<BulkLaunchResult>('/api/board/launch', {
        filter,
        step: plan.step,
        confirmed: true,
        ...(owner ? { owner } : {})
      });
      setPlan(null);
      setToast(
        `Launched ${result.launched.length} ${result.launched.length === 1 ? 'run' : 'runs'}.${result.failed.length ? ` ${result.failed.length} could not start.` : ''}`
      );
      reload();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Those could not be launched.');
    }
  }, [plan, filter, owner, reload]);

  const saveFilter = useCallback(async () => {
    if (!saveName?.trim()) return;
    try {
      await api.post('/api/saved-filters', {
        project_id: projectId,
        name: saveName.trim(),
        conditions: filter,
        shared: false
      });
      setSaveName(null);
      setToast('Saved. It is in your account menu under Saved filters.');
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'That could not be saved.');
    }
  }, [saveName, projectId, filter]);

  const toggleState = (key: PassState): void =>
    setFilter((held) => {
      const next = { ...held };
      if (held.state?.[0] === key) delete next.state;
      else next.state = [key];
      return next;
    });

  return (
    <Page
      eyebrow="MAESTRO · PROJECT BOARD"
      title={projectId}
      description="A VC pass is one virtual camera in one sequence. Each pass tracks its steps, the movie, and which edits use it."
      note="Columns come from this project's pipeline."
      action={
        can.launch && answer ? (
          <Select
            value=""
            onChange={(step) => step && void planBulk(step)}
            options={[
              { value: '', label: 'Launch for everything that matches…' },
              ...answer.columns.map((c) => ({ value: c.key, label: c.label }))
            ]}
          />
        ) : undefined
      }
    >
      {state.status === 'offline' && <OfflineBanner since={state.since} onRetry={reload} />}

      {(scope.data?.splats.length ?? 0) > 1 && (
        <Card>
          <Field label="Which splat">
            <Select
              value={filter.splat_id ?? ''}
              onChange={(splat_id) =>
                setFilter({ project_id: projectId, ...(splat_id ? { splat_id } : {}) })
              }
              options={[
                { value: '', label: 'Every splat in this project' },
                ...(scope.data?.splats ?? []).map((s) => ({
                  value: s.splat_id,
                  label: `${s.says} · version ${s.version}`
                }))
              ]}
            />
          </Field>
        </Card>
      )}

      {answer && (
        <Tiles>
          {STATE_TILES.map((tile) => (
            <Tile
              key={tile.key}
              count={answer.counts.state[tile.key] ?? 0}
              label={tile.label}
              swatch={tile.swatch}
              on={filter.state?.[0] === tile.key}
              onClick={() => toggleState(tile.key)}
            />
          ))}
          <Tile
            count={answer.counts.accepted}
            label="Stale, kept as it is"
            swatch={colour.staleWash}
            on={filter.staleness?.[0] === 'accepted'}
            onClick={() =>
              setFilter((held) => {
                const next = { ...held };
                if (held.staleness?.[0] === 'accepted') delete next.staleness;
                else next.staleness = ['accepted'];
                return next;
              })
            }
          />
          <Tile count={answer.total} label="VC passes in this splat" swatch={progressShade(0, 4)} />
        </Tiles>
      )}

      <Filters
        filter={filter}
        onChange={setFilter}
        view={view}
        onView={setView}
        answer={answer}
        people={people}
        sequences={scope.data?.sequences ?? []}
        vcs={scope.data?.vcs ?? []}
        canSave={can.save_filter}
        onSave={() => setSaveName('')}
      />

      {state.status === 'loading' && !answer && (
        <Loading says="Loading the project board…" rows={8} />
      )}
      {state.status === 'failed' && !answer && (
        <CouldNotLoad what="the project board" error={state.error} onRetry={reload} />
      )}

      {answer && answer.total === 0 && (
        <Empty
          title="No VC passes yet"
          says="They appear when a sequence uses a VC. Start by adding a capture and creating VCs."
        />
      )}

      {answer && answer.total > 0 && answer.matched === 0 && (
        <NoResults
          chips={answer.chips.map((c) => c.label)}
          onClear={() => setFilter({ project_id: projectId })}
        />
      )}

      {answer && answer.matched > 0 && (
        <>
          <Card
            title={view === 'by_vc' ? 'Every VC, across the sequences' : 'Every sequence, across the VCs'}
            aside="Darker means further along."
          >
            <Heatmap answer={answer} />
          </Card>

          <Rows
            answer={answer}
            canLaunch={can.launch && state.status !== 'offline'}
            onLaunch={(passId, stepKey) => void launchOne(passId, stepKey)}
            launching={launching}
            onOpenPass={(row: BoardRow) => onOpenPass(row._id)}
          />

          <span style={{ ...text.note, color: colour.inkSoft }}>
            Showing {answer.page.returned} of {answer.matched} matching passes.
            {answer.matched > answer.page.returned &&
              ' Narrow the filters to see the rest — the counts above are for all of them.'}
          </span>
        </>
      )}

      {plan && (
        <Dialog
          title={plan.result.needs_confirming ? 'Big job: confirm first' : `Launch ${plan.result.label}`}
          says={
            plan.result.needs_confirming
              ? `This will start ${plan.result.runs} runs. That is more than ${plan.result.confirm_above}, so Maestro asks first.`
              : `This will start ${plan.result.runs} ${plan.result.runs === 1 ? 'run' : 'runs'}.`
          }
          confirm={`Launch ${plan.result.runs}`}
          onConfirm={() => void confirmBulk()}
          onClose={() => setPlan(null)}
        >
          <Field label="Owner (who answers for the results)">
            <TextInput value={owner} onChange={setOwner} placeholder="Leave blank for the step's default owner" />
          </Field>
          {plan.result.skipped.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ ...text.smallStrong, color: colour.inkSoft }}>
                {plan.result.skipped.length} skipped
              </span>
              {plan.result.skipped.slice(0, 6).map((s) => (
                <span key={s.pass_id} style={{ ...text.note, color: colour.inkSoft }}>
                  {s.vc_id}: {s.reason}
                </span>
              ))}
              {plan.result.skipped.length > 6 && (
                <span style={{ ...text.note, color: colour.inkFaint }}>
                  and {plan.result.skipped.length - 6} more
                </span>
              )}
            </div>
          )}
          <span style={{ ...text.note, color: colour.inkSoft }}>
            Launching by hand resets the automatic re-run count for each step.
          </span>
        </Dialog>
      )}

      {saveName != null && (
        <Dialog
          title="Save this filter"
          says="It appears under Saved filters in your account menu, with a live count of what it matches."
          confirm="Save"
          onConfirm={() => void saveFilter()}
          onClose={() => setSaveName(null)}
        >
          <Field label="Name">
            <TextInput value={saveName} onChange={setSaveName} placeholder="Stale renders on proxy" />
          </Field>
        </Dialog>
      )}

      {toast && <DoneToast says={toast} onClose={() => setToast(null)} />}
    </Page>
  );
}
